import { Suspense, lazy, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ChevronDown, Building2 } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

// Immersive Experience
const ImmersiveExperience = lazy(() => import("@/components/immersive/ImmersiveExperience"));

// Cinematic Components
import CinematicHero from "@/components/cinematic/CinematicHero";
import VideoIntroLoader, { shouldShowVideoIntro, markVideoIntroShown } from "@/components/cinematic/VideoIntroLoader";

// Cinematic Home Components
import CinematicNavbar from "@/components/home/CinematicNavbar";
import TrustLogos from "@/components/home/TrustLogos";
import CinematicServicesGrid from "@/components/home/CinematicServicesGrid";
// CinematicAboutSection removed per user request
import CinematicProcess from "@/components/home/CinematicProcess";
import CinematicTestimonials from "@/components/home/CinematicTestimonials";
import CinematicFinalCTA from "@/components/home/CinematicFinalCTA";
import Footer from "@/components/Footer";


// SEO - Website Schema for homepage
import WebsiteSchema from "@/components/SEO/WebsiteSchema";
import { Helmet } from "react-helmet-async";

// FAQ Section - lazy load
const HomeFAQ = lazy(() => import("@/components/home/HomeFAQ"));

// Optimized loading placeholder
const OptimizedLoader = ({
  height = "h-20"
}: {
  height?: string;
}) => (
  <div className={`w-full ${height} flex items-center justify-center opacity-50 bg-cinema-dark`}>
    <div className="w-8 h-1 bg-white/10 rounded animate-pulse"></div>
  </div>
);

// Immersive loading screen
const ImmersiveLoader = () => (
  <div className="fixed inset-0 bg-black flex items-center justify-center">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      className="w-8 h-8 border-2 border-punto border-t-transparent rounded-full"
    />
  </div>
);

const Home = () => {
  const { locale } = useI18n();
  const [showVideoIntro, setShowVideoIntro] = useState(false);
  const [showImmersive, setShowImmersive] = useState(false);
  const [exitingImmersive, setExitingImmersive] = useState(false);

  // Check if we should show video intro on mount
  useEffect(() => {
    if (shouldShowVideoIntro()) {
      setShowVideoIntro(true);
    }
  }, []);

  const handleExitImmersive = () => {
    setExitingImmersive(true);
    setTimeout(() => {
      setShowImmersive(false);
      setExitingImmersive(false);
    }, 500);
  };

  // Enhanced structured data for local business
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService", "LegalService"],
    "name": "Punto Hispano",
    "legalName": "Punto Hispano Holding",
    "description": "Servicios profesionales en Suiza para hispanohablantes: gestoría, asesoría legal, seguros, vivienda, idiomas, relocation y soluciones digitales. Atención en español desde Zúrich.",
    "url": "https://puntohispano.ch",
    "logo": "https://lhfezfmidegmuwkdwjzt.supabase.co/storage/v1/object/public/img//Logo%20-%20Punto%20hispano%20(2).png",
    "image": "https://puntohispano.ch/lovable-uploads/7a9cdd5c-a694-4f25-8c2d-4cf56daf0a60.png",
    "telephone": "+41772317288",
    "priceRange": "€€",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Thurgauerstrasse 117, 1° Planta, Oficina 181",
      "addressLocality": "Opfikon",
      "addressRegion": "Zürich",
      "postalCode": "8152",
      "addressCountry": "CH"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "47.4245",
      "longitude": "8.5692"
    },
    "areaServed": [{
      "@type": "State",
      "name": "Zürich"
    }, {
      "@type": "Country",
      "name": "Switzerland"
    }],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "160",
      "bestRating": "5"
    },
  };

  // Show video intro first
  if (showVideoIntro) {
    return <VideoIntroLoader onComplete={() => setShowVideoIntro(false)} />;
  }

  // Show immersive experience
  if (showImmersive) {
    return (
      <>
        <AnimatePresence>
          {!exitingImmersive && (
            <motion.div
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Suspense fallback={<ImmersiveLoader />}>
                <ImmersiveExperience />
              </Suspense>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Skip button */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          onClick={handleExitImmersive}
          className="fixed bottom-8 right-8 z-50 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-full text-white/60 hover:text-white text-sm transition-all flex items-center gap-2"
        >
          <ChevronDown className="w-4 h-4" />
          Ver web completa
        </motion.button>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-cinema-dark text-white overflow-x-hidden">
      <Helmet>
        {locale === 'en' ? (
          <>
            <title>Punto Hispano · Spanish-Speaking Relocation & Admin in Zürich</title>
            <meta name="description" content="Move to Switzerland without the paperwork stress. Spanish-speaking experts in permits, taxes, insurance & housing. Free 10-min call. ★ 160+ Google reviews." />
          </>
        ) : (
          <>
            <title>Punto Hispano · Gestoría en Español en Suiza | Zúrich</title>
            <meta name="description" content="Trámites en Suiza sin dolores de cabeza: impuestos, permisos, seguros, vivienda y relocation en español. Consulta gratis 10 min. ★ 160+ reseñas Google." />
          </>
        )}
        <meta name="keywords" content="gestoría suiza español, trámites suiza hispanohablantes, relocation suiza, asesoría legal suiza, impuestos suiza" />
        <meta name="theme-color" content="#0a0a0a" />
        <link rel="canonical" href={locale === 'en' ? 'https://puntohispano.ch/en/' : 'https://puntohispano.ch/'} />
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>
      
      {/* Global Website Schema - Only on homepage */}
      <WebsiteSchema />
      


      
      {/* Cinematic Navbar */}
      <CinematicNavbar />
      
      {/* Cinematic Hero with curtain effect */}
      <CinematicHero />
      
      {/* Trust Logos Section */}
      <TrustLogos />
      
      {/* Services Grid */}
      <section id="servicios-section">
        <CinematicServicesGrid />
      </section>
      
      {/* About Section - Removed per user request */}
      
      {/* Process Film Strip */}
      <section id="process-section">
        <CinematicProcess />
      </section>
      
      {/* Testimonials */}
      <section id="testimonials-section">
        <CinematicTestimonials />
      </section>
      
      {/* FAQ */}
      <section id="faq-section" className="bg-cinema-dark">
        <Suspense fallback={<OptimizedLoader />}>
          <HomeFAQ />
        </Suspense>
      </section>
      
      {/* Final CTA */}
      <section id="cta-section">
        <CinematicFinalCTA />
      </section>
      
      {/* Cinematic Footer */}
      <Footer />
      
      {/* Floating button to immersive experience - HIDDEN per user request */}
      {/* 
      <Link to="/experiencia">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2, type: "spring" }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="fixed bottom-6 left-6 z-40 px-4 py-3 rounded-full bg-punto text-white shadow-lg hover:bg-punto/90 transition-colors flex items-center gap-2 group cursor-pointer"
        >
          <Building2 className="w-5 h-5" />
          <span className="text-sm font-medium hidden sm:inline">Experiencia 3D</span>
          <Sparkles className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
        </motion.div>
      </Link>
      */}
    </div>
  );
};

export default Home;
